import { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import Card from '@mui/material/Card';
import TableRow from '@mui/material/TableRow';
import { Box, Button, Container, IconButton, Stack } from '@mui/material';
import Page from '../../components/Page';
import { useAppSelector, useAppDispatch } from '../../store';
import {
  setLimit,
  setSkip,
  setCurrentPage,
  setStatus,
} from '../../store/settings/settingsSlice';
import {
  findSettings,
  createSettings,
  patchSettings,
} from '../../store/settings/api';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import { useFormik } from 'formik';
import useToggle from '../../hooks/useToggle';
import Scrollbar from '../../components/Scrollbar';
import Searchbar from '../../components/Searchbar';
import { omit } from 'lodash';
import {
  reset as resetSettings,
  setStatus as setSettingsStatus,
} from '../../store/settings/settingsSlice';
import SettingsFormModal from './SettingsFormModal';
import { Settings } from '../../store/settings/api/interface';
import settingSchema from '../../validation/settingSchema';

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, status, skip, isLoading } =
    useAppSelector((state) => state.settings);
  const [search, setSearch] = useState<string>('');
  const { toggle, onOpen, onClose } = useToggle();

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findSettings({
          limit,
          skip,
          ...(search && { search: search }),
        })
      );
    }
  }, [dispatch, limit, skip, status]);

  useEffect(() => {
    dispatch(resetSettings());
  }, [search]);

  const {
    handleSubmit,
    values,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
    setValues,
  } = useFormik({
    initialValues: {
      id: '',
      name: '',
      value: '',
      valueType: '',
      language: '',
      activeState: true,
    },
    validationSchema: settingSchema,
    onSubmit: async (values) => {
      try {
        if (values.id) {
          await dispatch(patchSettings(values)).unwrap();
        }
        if (!values.id) {
          const reqBody = omit(values, ['id']);
          await dispatch(createSettings(reqBody)).unwrap();
        }
        onClose();
        resetForm();
        dispatch(setStatus({ status: 'idle' }));
      } catch (error: any) {}
    },
  });

  const handleUpdate = (value: Settings): void => {
    onOpen();
    setValues({
      id: value.id,
      name: value?.name,
      value: value?.value ?? '',
      valueType: value?.valueType ?? '',
      language: value?.language ?? '',
      activeState: value?.activeState,
    });
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setSettingsStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  return (
    <>
      <Page title={'Settings: List'}>
        <Container maxWidth={'xl'}>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='flex-end'
            spacing={{ xs: 0.5, sm: 1.5 }}
            my={1}
          >
            <Button
              variant='contained'
              startIcon={<Iconify icon={'eva:plus-fill'} />}
              onClick={onOpen}
            >
              {'New Settings'}
            </Button>
          </Stack>

          <Card>
            <Searchbar handleSearch={handleSearch} />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
                <Table size={'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>{'Name'}</TableCell>
                      <TableCell align='center'>{'Type'}</TableCell>
                      <TableCell align='center'>{'Language'}</TableCell>
                      <TableCell align='center'>{'Value'}</TableCell>
                      <TableCell align='center'>{'Status'}</TableCell>
                      <TableCell align='center'>{'Action'}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoading ? (
                      <TableRow hover>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : data?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          {'No record found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.map((settings) => (
                        <TableRow key={settings.id}>
                          <TableCell align='center'>{settings?.name}</TableCell>
                          <TableCell align='center'>
                            {settings?.valueType}
                          </TableCell>
                          <TableCell align='center'>
                            {settings?.language}
                          </TableCell>
                          <TableCell align='center'>
                            {settings?.valueType === 'number'
                              ? settings?.value
                              : '-'}
                          </TableCell>
                          <TableCell align='center'>
                            <Label
                              variant='ghost'
                              color={settings.activeState ? 'success' : 'error'}
                            >
                              {settings.activeState ? 'Active' : 'InActive'}
                            </Label>
                          </TableCell>
                          <TableCell align='center'>
                            <IconButton
                              aria-label='edit'
                              onClick={() => handleUpdate(settings)}
                            >
                              <Iconify icon={'eva:edit-fill'} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>

            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component='div'
                count={totalRecords}
                rowsPerPage={limit}
                page={Math.min(Math.round(totalRecords / limit), currentPage)}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          </Card>
          <SettingsFormModal
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onClose={onClose}
            resetForm={resetForm}
            setFieldValue={setFieldValue}
            toggle={toggle}
            values={values}
          />
        </Container>
      </Page>
    </>
  );
};

export default Index;
